import {
  CheckCircleFilled,
  DownloadOutlined,
  EyeOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { useMutation, useQuery } from '@apollo/client';
import {
  GET_ME,
  GET_POST_BY_ID,
  Me,
  Post,
  RSVP_POST,
  RSVPUser,
} from '@frontend/data-access';
import {
  downloadICSFile,
  formatDate,
  stripHtmlTags,
} from '@frontend/shared/util';
import { Button, Checkbox, Divider, Input, Modal, Table } from 'antd';
import { CalendarEvent, google, outlook } from 'calendar-link';
import dayjs from 'dayjs';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

import EventInfo from './info';

/* eslint-disable-next-line */
export interface EventCardProps {
  id: string;
  type: 'ADMIN' | 'USER';
  customStyle?: string;
}

export function EventCard({ id, type = 'USER', customStyle }: EventCardProps) {
  const { t: tcommon } = useTranslation('common');
  const router = useRouter();
  const { publicRuntimeConfig } = getConfig();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [privacyCheck, setPrivacyCheck] = useState<boolean>(false);
  const [showMemberListModal, setShowMemberListModal] =
    useState<boolean>(false);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [memberData, setMemberData] = useState<RSVPUser[]>([]);

  const { data, loading, refetch } = useQuery<{ post: Post }>(GET_POST_BY_ID, {
    variables: { id },
    fetchPolicy: 'no-cache',
  });

  const post = data?.post;
  const rruleLine = useMemo(
    () => data?.post.recurrenceConfig?.match(/RRULE:.+/)?.[0] as string,
    [data?.post.recurrenceConfig]
  );

  const handleAddClickEvent = (type: 'download' | 'outlook' | 'google') => {
    if (post)
      switch (type) {
        case 'download':
          downloadICSFile(post);
          break;
        case 'google': {
          const event: CalendarEvent = {
            title: post.title,
            start: post.eventStartDate,

            end: post.eventEndDate,
            description: stripHtmlTags(post.body),
            location: post.eventLocation,
            allDay: post.allDay,
            rRule: rruleLine ? rruleLine.replace('RRULE:', '') : undefined,
          };
          const calendar = google(event);
          window.open(calendar, '_blank');
          break;
        }
        case 'outlook': {
          const event: CalendarEvent = {
            title: post.title,
            start: post.eventStartDate,
            end: post.eventEndDate,
            description: stripHtmlTags(post.body),
            location: post.eventLocation,
            allDay: post.allDay,
            rRule: rruleLine ? rruleLine.replace('RRULE:', '') : undefined,
          };
          const calendar = outlook(event);
          window.open(calendar, '_blank');
          break;
        }
        default:
          break;
      }
  };

  const [reserveEvent, { loading: reserveLoading }] = useMutation(RSVP_POST, {
    onCompleted: () => {
      refetch();
    },
  });

  const handleReserveEvent = () => {
    reserveEvent({ variables: { id } });
  };

  const { data: meData } = useQuery<{ me: Me }>(GET_ME, {
    onCompleted(data) {
      setIsLoggedIn(true);
    },
    onError(error) {
      setIsLoggedIn(false);
    },
  });

  const isOwner = useMemo(() => {
    return meData?.me?.id === post?.createdBy?.id;
  }, [meData?.me?.id, post?.createdBy?.id]);

  const renderJoinButton = (data: Post) => {
    if (type === 'USER') {
      if (dayjs().diff(data?.eventRegistrationDate, 'minutes') > 0) {
        return (
          <Button shape="round" className="w-full" disabled>
            {tcommon('event_registration_closed')}
          </Button>
        );
      }
      if (data?.rsvp === false) {
        return (
          <Button
            type="primary"
            shape="round"
            className="w-full"
            onClick={() => {
              if (isLoggedIn) {
                setShowModal(true);
              } else {
                router.push(
                  `/login?&referrer=${process.env['NEXT_PUBLIC_PLATFORM_URL']}${process.env['NEXT_PUBLIC_BASE_PATH']}${router.asPath}`
                );
              }
            }}
          >
            {tcommon('event_register')}
          </Button>
        );
      }
      if (data?.rsvp === true) {
        return (
          <Button shape="round" className="w-full" disabled>
            {tcommon('event_already_joined')}
          </Button>
        );
      }
    }
    return null;
  };

  const downloadMemberData = (data: string) => {
    const date = new Date();
    const filename = `member_list_${post?.title
      .replace(/ /g, '_')
      .toLowerCase()}_${date.toISOString().slice(0, 10)}.csv`;
    // const decodedString = Buffer.from(data, 'base64').toString('utf8');
    const blob = new Blob([data], {
      type: 'text/csv',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownload = () => {
    const csvRows = [];
    csvRows.push(`Name, Company, Email, Title, Registered date`);
    const str = memberData.map(
      (item) =>
        `${item.rsvpUser.fullName}, ${item.rsvpUser.companies
          ?.filter((c) => c.approvalStatus === 'approved')
          ?.map((item) => item.name)
          .join('; ')}, ${item.rsvpUser.email}, ${
          item?.rsvpUser.profile?.title
        }, ${item?.createdAt}`
    );
    csvRows.push(...str);
    downloadMemberData(csvRows.join('\r\n'));
  };

  useEffect(() => {
    if (post?.rsvpList && post?.rsvpList.length > 0) {
      setMemberData(post?.rsvpList);
    }
  }, [post?.rsvpList]);

  const renderDateExporter = () => {
    return (
      <>
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center gap-2 font-medium">
            <Image
              src={`${publicRuntimeConfig.staticFolder}/svg/google-calendar.svg`}
              alt="google-calendar"
              width={18}
              height={18}
            />
            {tcommon('event_register_success_add_to_google_calendar')}
          </div>
          <Button shape="round" onClick={() => handleAddClickEvent('google')}>
            {tcommon('event_register_success_add_event')}
          </Button>
        </div>
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center gap-2 font-medium">
            <Image
              src={`${publicRuntimeConfig.staticFolder}/svg/outlook.svg`}
              alt="outlook"
              width={18}
              height={18}
            />
            {tcommon('event_register_success_add_to_outlook')}
          </div>
          <Button shape="round" onClick={() => handleAddClickEvent('outlook')}>
            {tcommon('event_register_success_add_event')}
          </Button>
        </div>
        <div className="flex flex-row justify-between items-center">
          <div className="flex flex-row items-center gap-2 font-medium">
            <span className="text-lg">
              <FileOutlined />
            </span>
            {tcommon('event_register_success_download_ics_file')}
          </div>
          <Button shape="round" onClick={() => handleAddClickEvent('download')}>
            {tcommon('download')}
          </Button>
        </div>
      </>
    );
  };

  if (!post || loading) return null;

  return (
    <>
      <div>
        <Divider />
        <div
          className={`
            flex flex-col gap-4
            lg:grid lg:divide-x-2
            ${
              post?.rsvp && type === 'USER'
                ? 'lg:grid-cols-2'
                : 'lg:grid-cols-1'
            }
          `}
        >
          <div className={`rounded-xl space-y-4 ${customStyle}`}>
            <EventInfo
              title={
                type === 'ADMIN' ? tcommon('event_rsvp') : tcommon('event_date')
              }
              type={type}
              data={post}
            />
            {renderJoinButton(post)}
            {(type === 'ADMIN' || isOwner) && (
              <div className="flex flex-wrap gap-4">
                <Button
                  shape="round"
                  className="w-full"
                  type={type === 'ADMIN' ? 'primary' : 'default'}
                  onClick={() => setShowMemberListModal(true)}
                >
                  <EyeOutlined /> {tcommon('event_see_member_list')}
                </Button>
                <Button
                  shape="round"
                  className="w-full"
                  onClick={handleDownload}
                >
                  <DownloadOutlined /> {tcommon('event_download_email_list')}
                </Button>
              </div>
            )}
          </div>
          {post?.rsvp && type === 'USER' && (
            <div className="space-y-4 lg:pl-5">{renderDateExporter()}</div>
          )}
        </div>
      </div>
      {type === 'USER' && (
        <Modal
          title={
            post?.rsvp
              ? tcommon('event_register_done')
              : tcommon('event_register_confirmation')
          }
          open={showModal}
          onCancel={() => setShowModal(false)}
          footer={
            !post?.rsvp
              ? [
                  <Button
                    shape="round"
                    key="cancel"
                    onClick={() => setShowModal(false)}
                  >
                    {tcommon('cancel')}
                  </Button>,
                  <Button
                    shape="round"
                    key="submit"
                    type="primary"
                    disabled={!privacyCheck}
                    onClick={() => handleReserveEvent()}
                    loading={reserveLoading}
                  >
                    {tcommon('event_register')}
                  </Button>,
                ]
              : [
                  <Button
                    shape="round"
                    key="close"
                    onClick={() => setShowModal(false)}
                  >
                    {tcommon('close')}
                  </Button>,
                ]
          }
        >
          <div className="space-y-4">
            {!post?.rsvp ? (
              <>
                <EventInfo type={type} title={post.title} data={post} />
                <Checkbox onChange={(e) => setPrivacyCheck(e.target.checked)}>
                  <div className="text-sm font-normal text-gray-700">
                    {tcommon('event_privacy_notice')}
                    <Link
                      href="https://scventures.io/terms-and-conditions/"
                      className="text-primary-400 cursor-pointer"
                    >
                      {tcommon('terms_of_service')}
                    </Link>
                    ,{' '}
                    <Link
                      href="https://scventures.io/privacy-policy/"
                      className="text-primary-400 cursor-pointer"
                    >
                      {tcommon('privacy_policy')}
                    </Link>{' '}
                    {tcommon('and')}{' '}
                    <Link
                      href="https://www.sc.com/en/cookie-policy/"
                      className="text-primary-400 cursor-pointer"
                    >
                      {tcommon('cookies_policy')}
                    </Link>
                    . {tcommon('event_photography_notice')}
                  </div>
                </Checkbox>
              </>
            ) : (
              <div className="text-center space-y-4">
                <div className="text-green-600">
                  <CheckCircleFilled className="mr-4  text-7xl" />
                </div>
                <h3 className="text-2xl font-semibold">
                  {tcommon('event_register_success_title')}
                </h3>
                <div className="text-base whitespace-pre">
                  {tcommon('event_register_success_message')}
                </div>
                <Divider />
                <div className="text-left">
                  {tcommon('event_register_success_add_to_calendar')}
                </div>
                {renderDateExporter()}
                <Divider />
              </div>
            )}
          </div>
        </Modal>
      )}
      {(type === 'ADMIN' || isOwner) && (
        <Modal
          title={tcommon('member_list')}
          open={showMemberListModal}
          onCancel={() => setShowMemberListModal(false)}
          footer={false}
          width={1100}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-end space-x-4">
              <Button shape="round" onClick={() => handleDownload()}>
                <DownloadOutlined /> {tcommon('event_download_email_list')}
              </Button>
              <Input.Search
                placeholder={tcommon('event_register_search_name')}
                onSearch={(value) => {
                  const result = post.rsvpList?.filter((item) =>
                    item.rsvpUser.fullName
                      .toLowerCase()
                      .includes(value.toLowerCase())
                  );
                  setMemberData(result);
                }}
                onChange={(e) => {
                  const result = post.rsvpList?.filter((item) =>
                    item.rsvpUser.fullName
                      .toLowerCase()
                      .includes(e.target.value.toLowerCase())
                  );
                  setMemberData(result);
                }}
                style={{ width: 300 }}
                size="middle"
                enterButton
                allowClear
              />
            </div>
            <Table
              columns={[
                {
                  title: tcommon('event_register_name') as string,
                  dataIndex: ['rsvpUser', 'fullName'],
                  key: 'fullName',
                  width: 200,
                  ellipsis: true,
                },
                {
                  title: tcommon('event_register_company') as string,
                  dataIndex: ['rsvpUser', 'company'],
                  key: 'company',
                  width: 250,
                  ellipsis: true,
                  render: (_, record) => {
                    const companies = record.rsvpUser.companies
                      ?.filter((c) => c.approvalStatus === 'approved')
                      .map((c) => c.name);
                    return companies?.join(', ');
                  },
                },
                {
                  title: tcommon('event_register_email') as string,
                  dataIndex: ['rsvpUser', 'email'],
                  key: 'email',
                  width: 250,
                  ellipsis: true,
                },
                {
                  title: tcommon('title') as string,
                  dataIndex: 'title',
                  key: 'title',
                  width: 200,
                  ellipsis: true,
                  render: (_, record) => record?.rsvpUser.profile?.title,
                },
                {
                  title: tcommon('event_registered_date') as string,
                  dataIndex: 'createdAt',
                  key: 'createdAt',
                  width: 200,
                  ellipsis: true,
                  render: (date) =>
                    date && formatDate(date, 'DD MMM YYYY, HH:mm'),
                },
              ]}
              dataSource={memberData}
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} of ${total} items`,
              }}
            />
          </div>
        </Modal>
      )}
    </>
  );
}

export default EventCard;
